import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Calculator, Dumbbell, ChevronRight, Hexagon } from 'lucide-react';

const Resources = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  const resources = [
    {
      icon: Hexagon,
      title: "Longevity Polygon Assessment",
      description: "Assess your fitness across 5 key areas and visualise how balanced and sustainable your training is for long-term health.",
      link: "/resources/longevity-assessment",
      tag: "Featured"
    },
    {
      icon: Calculator,
      title: "Body Fat Calculator",
      description: "Calculate your body fat percentage using the US Navy method. Track your progress with accurate measurements.",
      link: "/resources/body-fat-calculator",
      tag: "Tool"
    },
    {
      icon: Dumbbell,
      title: "One Rep Max Calculator",
      description: "Estimate your one-rep max based on the weight you can lift for multiple reps. Plan your training loads effectively.",
      link: "/resources/one-rep-max-calculator",
      tag: "Tool"
    },
    {
      icon: Camera,
      title: "How To Take Progress Photos",
      description: "Learn how to take consistent before and after photos to track your body composition changes.",
      link: "/client/photo-guide",
      tag: "Guide"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F0EB]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#1A1A1A]">
        <div className={`container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-3xl">
            <span className="text-[#C9A962] text-sm font-semibold tracking-[0.1em] uppercase mb-4 block">Client Resources</span>
            <h1 className="heading-display text-white mb-6">Resources</h1>
            <p className="text-lg md:text-xl text-white/90">
              Tools and guides to help you track your progress and get the most out of your training.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {resources.map((resource, index) => {
                const Icon = resource.icon;
                return (
                  <Link
                    key={index}
                    to={resource.link}
                    className="bg-white p-8 group hover:shadow-lg transition-all duration-300 border border-transparent hover:border-[#C9A962]"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center">
                        <Icon className="text-[#C9A962]" size={24} />
                      </div>
                      <span className="text-xs font-semibold tracking-[0.1em] uppercase text-[#C9A962] bg-[#F5F0EB] px-3 py-1">
                        {resource.tag}
                      </span>
                    </div>
                    <h3 className="heading-small mb-3 group-hover:text-[#C9A962] transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-[#6B6B6B] mb-4">
                      {resource.description}
                    </p>
                    <div className="flex items-center text-[#C9A962] text-sm font-semibold tracking-wide uppercase">
                      <span>View Resource</span>
                      <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A1A1A] py-16">
        <div className="container text-center">
          <h2 className="heading-medium text-white mb-4">Need Help With Your Training?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            If you have questions about tracking your progress or need personalised guidance, get in touch.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#C9A962] text-[#1A1A1A] px-8 py-4 text-sm font-semibold tracking-[0.1em] uppercase hover:bg-[#d4b872] transition-colors"
          >
            Contact Me
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Resources;
